import type { LockedModuleKey } from '@diagon-alley/common';
import { ReparoApi } from './index';

/** Opening state for one Inventory tab. `rollupBy` is a key from `constants/inventoryRollup`. */
export type InventoryTabDefaults = {
    rollupBy?: string;
};

/** SKU Ageing adds the age-bucket upper edges (days, ascending) to the shared rollup default. */
export type AgeingTabDefaults = InventoryTabDefaults & {
    /** The SKU Ageing tab is shown to this org only when true (staged rollout). */
    enabled?: boolean;
    bucketEdges?: number[];
};

/**
 * Per-organisation Inventory grid defaults. Only `drr` (the DoH View) is read today;
 * the sibling tabs are reserved. Null/absent means "no brand default".
 */
/**
 * Replenishment -> Inventory health. Two levels, and its own grouping vocabulary — see
 * `constants/replenishmentGrouping` for why it cannot share the Inventory rollup keys.
 */
export type ReplenishmentHealthDefaults = {
    groupBy?: string;
    subGroupBy?: string;
};

export type InventoryViewDefaults = {
    version: 1;
    drr?: InventoryTabDefaults;
    ageing?: AgeingTabDefaults;
    snapshot?: InventoryTabDefaults;
    planning?: InventoryTabDefaults;
    pipeline?: InventoryTabDefaults;
    replenishmentHealth?: ReplenishmentHealthDefaults;
};

/** Sales page settings. `bundleReporting` is switched on brand by brand; absent = off. */
export type SalesViewDefaults = {
    bundleReporting?: {
        enabled?: boolean;
        defaultView?: 'byProduct' | 'asSold';
    };
};

/**
 * How this brand's products are physically sized. Drives which measurement field the
 * product form offers — the backend rejects the other one.
 */
export type MeasurementBasis = 'WEIGHT' | 'VOLUME' | 'BOTH';

export type OrgConfig = {
    id: string;
    name: string;
    /** ISO 4217 code (INR, AED, …) — display currency for all monetary values. */
    currency?: string;
    warehouseOpsEnabled: boolean;
    /** Selling to B2B customers is on; shows B2B Customers in the menu. */
    b2bEnabled?: boolean;
    /**
     * Module keys outside the brand's plan (LOCKED_MODULE_KEYS in common). The sidebar shows
     * them under Premium and the router refuses them. Missing or empty = the whole product.
     */
    lockedModules?: LockedModuleKey[];
    revenueMode?: string;
    measurementBasis?: MeasurementBasis;
    wmsIntegration?: unknown;
    inventoryViewDefaults?: InventoryViewDefaults | null;
    salesViewDefaults?: SalesViewDefaults | null;
    drrExcludeStockoutDays?: boolean;
    /**
     * Which item classes carry batch / expiry on the way in. Null or missing = off everywhere:
     * the Add Shipment form shows its batch columns only for a class marked 'full'.
     */
    batchTracking?: BatchTracking | null;
} & BrandLetterhead;

export type BatchTrackingItemClass = 'RM' | 'PM' | 'FG';
export type BatchTrackingMode = 'full' | 'off';
export type BatchTracking = Partial<Record<BatchTrackingItemClass, BatchTrackingMode>>;

/**
 * What a document says about who issued it.
 *
 * All optional and all null until somebody fills the form in — every reader has to render
 * sensibly without them, because no organisation has any of this today.
 */
/** A document type that carries a brand-defined number series. */
export type NumberedDocumentType = 'PRODUCTION_ORDER' | 'PURCHASE_ORDER' | 'SALES_ORDER';

export type DocumentNumberingPrefix = {
    /** Exactly what the brand typed - 'BANGE-', 'TRVY/PO/'. Nothing is appended. */
    value: string;
    /** The one a new document starts with. */
    isDefault?: boolean;
};

/**
 * How one document type is numbered.
 *
 * Each prefix counts on its own, so BANGE-0007 and FLIEGER-0002 sit side by side without one
 * pushing the other along. No count is stored anywhere - the next number is read off the
 * documents that exist.
 */
export type DocumentNumberingConfig = {
    prefixes?: DocumentNumberingPrefix[];
    padWidth?: number;
};

export type DocumentNumbering = Partial<
    Record<NumberedDocumentType, DocumentNumberingConfig>
>;

export type DocumentBankDetails = {
    accountName?: string | null;
    accountNumber?: string | null;
    ifsc?: string | null;
    bankName?: string | null;
    branch?: string | null;
    upiId?: string | null;
};

export type BrandLetterhead = {
    /** Bank account printed on B2B proforma invoices. */
    bankDetails?: DocumentBankDetails | null;
    /**
     * The registered entity a document's Bill To block names, as against `OrgConfig.name`,
     * which is the trading name every screen shows. Readers fall back to the trading name.
     */
    legalName?: string | null;
    /** The brand's own billing contact, as printed. */
    email?: string | null;
    gstin?: string | null;
    /** Corporate Identity Number, printed under the GSTIN. Companies only. */
    cin?: string | null;
    address?: string | null;
    phone?: string | null;
    /** GST state code — '06', not 'Haryana'. */
    stateCode?: string | null;
    website?: string | null;
    /** An S3 key, not something a browser can load. Use `getBrandLogoUrl` to display it. */
    logo?: string | null;
    /**
     * The standard terms that seed a new purchase or production order.
     *
     * READ-ONLY ON THE WAY OUT, WRITTEN AS `termsAndConditions` ON THE WAY IN — the server keeps
     * these in a jsonb of document defaults and flattens them onto the DTO, so the shape you read
     * is not the shape you send.
     */
    documentTerms?: string | null;

    /**
     * The brand's own order-number formats, keyed by document type.
     *
     * Null until somebody opens the numbering screen, which is every brand today - and every
     * reader falls back to the built-in format, so null is a working state rather than a gap.
     */
    documentNumbering?: DocumentNumbering | null;
    /** An S3 key. Use `getBrandSignatureUrl` to display it. */
    signature?: string | null;
    /** The words printed with the signature, beneath the image. */
    signatoryName?: string | null;
    signatoryDesignation?: string | null;
};

export const getOrgConfig = (organizationId: string): Promise<OrgConfig> =>
    ReparoApi<OrgConfig>({
        method: 'GET',
        url: `/organizations/${organizationId}`,
    });

export const setWarehouseOpsEnabled = (organizationId: string, enabled: boolean): Promise<OrgConfig> =>
    ReparoApi<OrgConfig>({
        method: 'PATCH',
        url: `/organizations/${organizationId}`,
        body: JSON.stringify({ warehouseOpsEnabled: enabled }),
    });

/**
 * Exclude confirmed out-of-stock days from the DRR denominator, org-wide.
 *
 * Separate call rather than a field on the view-defaults payload: this changes what the
 * numbers MEAN, not how the grid opens, and it moves every DRR, DoH, reorder point and
 * plan quantity at once.
 */
export const setDrrExcludeStockoutDays = (
    organizationId: string,
    enabled: boolean,
): Promise<OrgConfig> =>
    ReparoApi<OrgConfig>({
        method: 'PATCH',
        url: `/organizations/${organizationId}`,
        body: JSON.stringify({ drrExcludeStockoutDays: enabled }),
    });

/**
 * Merge one tab's grouping into the org's existing defaults.
 *
 * Settings only edits `drr` today, but the column holds every tab — so this must write
 * through the keys it does not manage rather than replacing the object, or saving from
 * one tab would silently drop another's default.
 */
/** Writes one surface's settings through, leaving every other surface's untouched. */
const mergeViewDefaults = (
    existing: InventoryViewDefaults | null | undefined,
    surface: keyof Omit<InventoryViewDefaults, 'version'>,
    settings: InventoryTabDefaults | AgeingTabDefaults | ReplenishmentHealthDefaults,
): InventoryViewDefaults => ({
    ...(existing ?? {}),
    version: 1,
    [surface]: settings,
});

export const buildInventoryViewDefaults = (
    existing: InventoryViewDefaults | null | undefined,
    tab: keyof Omit<InventoryViewDefaults, 'version'>,
    rollupBy: string,
): InventoryViewDefaults => mergeViewDefaults(existing, tab, { rollupBy });

/**
 * Replenishment stores a pair rather than a single dimension. Kept as its own builder
 * because the two payloads are genuinely different shapes, not because the merge differs.
 */
export const buildReplenishmentHealthDefaults = (
    existing: InventoryViewDefaults | null | undefined,
    groupBy: string,
    subGroupBy: string,
): InventoryViewDefaults =>
    mergeViewDefaults(existing, 'replenishmentHealth', { groupBy, subGroupBy });

/**
 * The SKU Ageing bucket edges. Writes through the tab's existing rollup default, so setting
 * the edges never drops a grouping chosen earlier (or vice versa).
 */
export const buildInventoryAgeingDefaults = (
    existing: InventoryViewDefaults | null | undefined,
    bucketEdges: number[] | undefined,
): InventoryViewDefaults =>
    mergeViewDefaults(existing, 'ageing', { ...(existing?.ageing ?? {}), bucketEdges });

export const setInventoryViewDefaults = (
    organizationId: string,
    inventoryViewDefaults: InventoryViewDefaults | null,
): Promise<OrgConfig> =>
    ReparoApi<OrgConfig>({
        method: 'PATCH',
        url: `/organizations/${organizationId}`,
        body: JSON.stringify({ inventoryViewDefaults }),
    });

/**
 * Save the letterhead fields.
 *
 * NO ORGANISATION ID IN THE PATH, and that is the point rather than a convenience. `PATCH
 * /organizations/:id` — which every other setting on this screen uses — is unauthenticated, so
 * anybody who knows an id can rewrite that tenant's record. What a brand's documents say about
 * themselves does not go through that door; the server takes the organisation from the token.
 *
 * The logo is NOT here either: it is a file with its own multipart endpoint, and sending its key
 * as text would let a caller point this organisation at another's object.
 *
 * A field sent as null is cleared; omitted leaves it alone.
 */
/**
 * What the letterhead screen may write.
 *
 * NOT `BrandLetterhead` MINUS THE LOGO ANY MORE. The terms read back as `documentTerms` (the
 * server flattens them off a jsonb) but are written as `termsAndConditions`, so the two shapes
 * genuinely differ and deriving one from the other would send a field the server ignores.
 */
export type BrandLetterheadPayload = Omit<
    BrandLetterhead,
    'logo' | 'documentTerms' | 'signature' | 'documentNumbering'
> & {
    /** Null or blank clears the default. */
    termsAndConditions?: string | null;
    /**
     * Sent whole, and replaces the stored list for the types it names.
     *
     * Types left out are untouched, so saving production-order prefixes cannot drop the
     * purchase-order ones - which matters because the screen edits one type at a time.
     */
    documentNumbering?: DocumentNumbering | null;
};

export const updateBrandLetterhead = (
    letterhead: BrandLetterheadPayload,
): Promise<OrgConfig> =>
    ReparoApi<OrgConfig>({
        method: 'PATCH',
        url: '/organizations/letterhead',
        body: JSON.stringify(letterhead),
    });

/**
 * Upload the brand logo.
 *
 * No organisation id in the path: the server takes it from the token, so there is nothing to
 * tamper with. Whatever lands at that key is printed on every document this brand issues.
 */
export const uploadBrandLogo = async (file: File): Promise<{ key: string }> => {
    const body = new FormData();
    body.append('file', file);

    return ReparoApi<{ key: string }>({
        method: 'POST',
        url: '/organizations/logo',
        body,
    });
};

/** A short-lived link to the logo, for a screen. Null when none is set. */
export const getBrandLogoUrl = (): Promise<{ url: string | null }> =>
    ReparoApi<{ url: string | null }>({
        method: 'GET',
        url: '/organizations/logo',
    });

/**
 * The brand's signature, uploaded once and stamped on every document it issues.
 *
 * A facsimile above "Authorised Signatory" — the convention on an Indian commercial document,
 * not an electronic signature. Admin only, as the logo is.
 */
export const uploadBrandSignature = async (file: File): Promise<{ key: string }> => {
    const body = new FormData();
    body.append('file', file);

    return ReparoApi<{ key: string }>({
        method: 'POST',
        url: '/organizations/signature',
        body,
    });
};

export const getBrandSignatureUrl = (): Promise<{ url: string | null }> =>
    ReparoApi<{ url: string | null }>({
        method: 'GET',
        url: '/organizations/signature',
    });

/** The brand's movement rulebook: WMS push policy + batch picking policy. */
export type WmsPushPolicyInput = {
    purchaseOrder?: 'PO_AT_ORDER' | 'PO_PER_SHIPMENT' | null;
    grnAt?: 'SHIPMENT' | 'RECEIPT' | null;
    stn?: 'GATE_PASS' | null;
    distribution?: 'SALE_ORDER' | 'GATE_PASS' | null;
};

export type OrgPolicies = {
    wmsType: string | null;
    pushPolicy: WmsPushPolicyInput | null;
    batchPickingPolicy: 'FEFO' | 'FIFO';
};

export const getOrgPolicies = (): Promise<OrgPolicies> =>
    ReparoApi<OrgPolicies>({ method: 'GET', url: '/organizations/policies' });

/** Partial update; a pushPolicy key sent as null clears it. Admins only. */
export const updateOrgPolicies = (payload: {
    pushPolicy?: WmsPushPolicyInput;
    batchPickingPolicy?: 'FEFO' | 'FIFO';
}): Promise<OrgPolicies> =>
    ReparoApi<OrgPolicies>({
        method: 'PATCH',
        url: '/organizations/policies',
        body: JSON.stringify(payload),
    });

/**
 * Who this brand has asked to be emailed when a manufacturer acknowledges a production order.
 *
 * NO ORGANISATION ID IN THE PATH, for the same reason the letterhead routes have none: the
 * server takes the organisation from the token, and a recipient list decides where this
 * brand's production data lands.
 *
 * `enabled` is separate from an empty `to` so a brand can pause notifications for a week
 * without cutting and re-pasting its recipient list. Either one being off means nothing sends.
 *
 * The manufacturer's own address is not here — it is read from the supplier record at send
 * time and copied in, so it cannot drift from the address the order itself was sent to.
 */
export interface NotificationRecipients {
    enabled: boolean;
    to: string[];
    cc: string[];
    /**
     * Per-manufacturer overrides, for the events whose sender knows which site a message is
     * about - receipt QC today. A site's To replaces the list above for its deliveries; its CC
     * adds. Only the card for such an event shows this block.
     */
    byVendor?: Record<string, { to?: string[]; cc?: string[] }>;
}

/**
 * The events whose recipients are one brand-wide list rather than a per-vendor book.
 *
 * The URL words, not the stored keys. `material-qc` is raw material ruled on at a
 * manufacturer's gate; `product-qc` is finished goods, which the brand signs off rather than
 * the site. They are separate lists because at most brands those are separate desks.
 */
export type BrandWideNotification =
    | 'order-acknowledged'
    | 'material-qc'
    | 'receipt-qc'
    | 'product-qc';

export const getNotificationEmails = (
    event: BrandWideNotification,
): Promise<NotificationRecipients> =>
    ReparoApi<NotificationRecipients>({
        method: 'GET',
        url: `/organizations/notifications/${event}`,
    });

/** Admin only — the server refuses anyone else, the same as the letterhead. */
export const updateNotificationEmails = (
    event: BrandWideNotification,
    recipients: NotificationRecipients,
): Promise<NotificationRecipients> =>
    ReparoApi<NotificationRecipients>({
        method: 'PATCH',
        url: `/organizations/notifications/${event}`,
        body: JSON.stringify(recipients),
    });

/**
 * Who is copied when an order document is emailed to its counterparty.
 *
 * TWO LEVELS. `alwaysCc` goes on every one of these emails; `byVendor` adds the people who
 * matter for one counterparty. The same shape the distribution plan email uses, keyed by
 * vendor rather than destination warehouse.
 *
 * `to` is only meaningful for a production order, whose recipient is resolved live from the
 * supplier record. A purchase order's recipient is `vendor_email` on the order itself, shown
 * on its form and printed on the PDF — the server drops a `to` saved against it rather than
 * letting a setting quietly rival a field the sender can see.
 *
 * THESE SEED A DIALOG, they do not decide a send. Both order emails are issued by a person who
 * reviews the recipients first, so a configured address they removed stays removed.
 */
export interface OrderEmailRecipients {
    alwaysCc: string[];
    byVendor: Record<string, { to?: string[]; cc?: string[] }>;
}

/** Which order document's recipient book. The URL words, not the stored key. */
export type OrderEmailDocument = 'production-order' | 'purchase-order';

export const getOrderEmailRecipients = (
    document: OrderEmailDocument,
): Promise<OrderEmailRecipients> =>
    ReparoApi<OrderEmailRecipients>({
        method: 'GET',
        url: `/organizations/notifications/order-emails/${document}`,
    });

/** Admin only — the server refuses anyone else, the same as the letterhead. */
export const updateOrderEmailRecipients = (
    document: OrderEmailDocument,
    recipients: OrderEmailRecipients,
): Promise<OrderEmailRecipients> =>
    ReparoApi<OrderEmailRecipients>({
        method: 'PATCH',
        url: `/organizations/notifications/order-emails/${document}`,
        body: JSON.stringify(recipients),
    });

/**
 * The saved defaults for one order to one vendor, as the send dialog should open.
 *
 * ONE PLACE COMPUTES THE RULE, because both send dialogs need it and re-deriving
 * "alwaysCc plus this vendor's cc" by hand in each is how the two drift. Lives on the client
 * because it seeds a form — the server never applies these, or the dialog would stop being
 * the truth about who is being emailed.
 */
export const orderEmailDefaultsFor = (
    recipients: OrderEmailRecipients | undefined,
    vendorId: string | null | undefined,
): { to: string[]; cc: string[] } => {
    if (!recipients) return { to: [], cc: [] };
    const forVendor = vendorId ? recipients.byVendor?.[vendorId] : undefined;

    return {
        to: forVendor?.to ?? [],
        cc: [...(recipients.alwaysCc ?? []), ...(forVendor?.cc ?? [])],
    };
};
